/**
 * i18n minimalista, con el ESPAÑOL COMO CLAVE. La app nació en español y todo el texto está en el
 * código; en vez de inventar claves para cientos de cadenas, la propia cadena en español es la
 * clave y el diccionario `EN` da su inglés. `t("Mis piscinas")` devuelve "My pools" en inglés y
 * "Mis piscinas" en español — y si una cadena aún no está traducida, cae al español en vez de
 * enseñar un hueco. Así se puede traducir por tandas sin romper nada.
 *
 * Para interpolar: `t("Te quedan {n} reajustes", { n: 5 })` — los `{x}` se sustituyen tras traducir.
 */
import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { EN } from "./traducciones";

export type Idioma = "es" | "en";

const Ctx = createContext<{ idioma: Idioma; setIdioma: (i: Idioma) => void }>({
  idioma: "es",
  setIdioma: () => {},
});

function inicial(): Idioma {
  try {
    const g = localStorage.getItem("idioma");
    if (g === "en" || g === "es") return g;
  } catch { /* sin storage */ }
  return typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("en")
    ? "en"
    : "es";
}

export function IdiomaProvider({ children }: { children: ReactNode }) {
  const [idioma, setEstado] = useState<Idioma>(inicial);
  const setIdioma = (i: Idioma) => {
    setEstado(i);
    try { localStorage.setItem("idioma", i); } catch { /* sin storage */ }
  };
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = idioma;
  }, [idioma]);
  return <Ctx.Provider value={{ idioma, setIdioma }}>{children}</Ctx.Provider>;
}

export function useIdioma() {
  return useContext(Ctx);
}

/** Devuelve la función `t`: traduce del español al idioma activo, con interpolación opcional. */
export function useT() {
  const { idioma } = useContext(Ctx);
  return (es: string, vars?: Record<string, string | number>) => {
    let s = idioma === "en" ? (EN[es] ?? es) : es;
    if (vars) for (const k in vars) s = s.replaceAll(`{${k}}`, String(vars[k]));
    return s;
  };
}
